import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Sparkles,
  User,
  Linkedin,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Calendar,
  BookOpen,
  ListOrdered,
  Settings2,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ImageIcon,
  Lock,
  Film,
  Play,
  Eye,
  Edit2,
} from "lucide-react";
import { workshopsService } from "@/api/workshopsService";
import { linkedinService, getLinkedInAvatarUrl } from "@/api/linkedinService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const WORKSHOP_MODES = ["Online", "Offline", "Hybrid"];
const WORKSHOP_STATUSES = ["Published", "Draft", "Completed", "Cancelled"];

const BLANK_INSTRUCTOR = {
  id: "",
  name: "",
  role: "",
  bio: "",
  avatar_url: "",
  linkedin_url: "",
  is_lead: false,
};

const BLANK_HIGHLIGHT = {
  id: null,
  media_type: "highlight",
  media_format: "image",
  storage_path: "",
  video_url: "",
  thumbnail_url: "",
  alt_text: "",
  caption: "",
  description: "",
  display_order: 0,
  is_published: true,
  is_featured: false,
};

function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i
  );
  if (ytMatch && ytMatch[1]) {
    return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
  }
  const vimeoMatch = url.match(
    /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|)(\d+)(?:$|\/|\?)/i
  );
  if (vimeoMatch && vimeoMatch[3]) {
    return `https://player.vimeo.com/video/${vimeoMatch[3]}?autoplay=1`;
  }
  return null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function generateSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// ── Tab Button ─────────────────────────────────────────────────────────────

function TabButton({ id, active, onClick, icon: Icon, label, dot }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors whitespace-nowrap",
        active
          ? "bg-cyan-50 text-cyan-700 border border-cyan-200 shadow-xs"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
      )}
    >
      <Icon className="w-3.5 h-3.5 text-cyan-600" />
      <span>{label}</span>
      {dot && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
    </button>
  );
}

// ── Agenda Item Row ────────────────────────────────────────────────────────

function AgendaItemRow({ item, index, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3 shadow-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          Item {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            className="p-1 rounded text-slate-400 hover:text-cyan-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            title="Move up"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMoveDown(index)}
            disabled={index === total - 1}
            className="p-1 rounded text-slate-400 hover:text-cyan-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            title="Move down"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 rounded text-rose-600 hover:bg-rose-50 transition-colors"
            title="Remove item"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-700">Topic Title</Label>
        <Input
          value={item.topic}
          onChange={(e) => onChange(index, "topic", e.target.value)}
          placeholder="e.g. Introduction to AI Engineering"
          className="h-9 rounded-xl bg-white border-slate-200 text-slate-900 text-xs"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-700">Description (optional)</Label>
        <Textarea
          rows={2}
          value={item.description}
          onChange={(e) => onChange(index, "description", e.target.value)}
          placeholder="Brief summary of what will be covered in this segment..."
          className="rounded-xl bg-white border-slate-200 text-slate-900 text-xs leading-relaxed"
        />
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function AdminWorkshopEditor() {
  const navigate = useNavigate();
  const { slug: paramSlug, id: paramId } = useParams();
  const identifier = paramSlug || paramId;
  const isEditMode = Boolean(identifier);
  const [currentWorkshopId, setCurrentWorkshopId] = useState(paramId || null);

  const [activeTab, setActiveTab] = useState("instructor");
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);

  // ── Tab 1: Instructors list ───────────────────────────────────────────────
  // Each entry: { id, name, role, bio, avatar_url, linkedin_url, is_lead }
  const [instructors, setInstructors] = useState([
    { ...BLANK_INSTRUCTOR, name: "Shiveda AI Engineering Team", role: "Senior AI Engineers & Architects", is_lead: true },
  ]);
  const [fetchingLinkedInIdx, setFetchingLinkedInIdx] = useState(null);

  // ── Tab 2: Basic & Schedule fields ───────────────────────────────────────
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [mode, setMode] = useState("Online");
  const [venue, setVenue] = useState("Live Interactive Session (Virtual)");
  const [date, setDate] = useState("2026-09-20");
  const [startTime, setStartTime] = useState("07:00 PM");
  const [endTime, setEndTime] = useState("08:30 PM");
  const [duration, setDuration] = useState("1.5 Hours");
  const [status, setStatus] = useState("Published");

  // ── Tab 3: Curriculum fields ──────────────────────────────────────────────
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [learningOutcomesText, setLearningOutcomesText] = useState("");
  const [prerequisitesText, setPrerequisitesText] = useState("");

  // ── Tab 4: Agenda items ───────────────────────────────────────────────────
  const [agendaItems, setAgendaItems] = useState([]);

  // ── Tab 5: Internal & Settings ────────────────────────────────────────────
  const [meetingLink, setMeetingLink] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [registrationDeadline, setRegistrationDeadline] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [certificateAvailable, setCertificateAvailable] = useState(true);
  const [coverImage, setCoverImage] = useState(
    "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&auto=format&fit=crop&q=80"
  );
  const [isFree, setIsFree] = useState(true);
  const [price, setPrice] = useState(0);
  const [capacity, setCapacity] = useState(150);

  // ── Tab 6: Promotional Modal & Highlights Settings ───────────────────────
  const [showInPromotionalModal, setShowInPromotionalModal] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);

  // Highlights state
  const [highlights, setHighlights] = useState([]);
  const [loadingHighlights, setLoadingHighlights] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState(null);
  const [isHighlightModalOpen, setIsHighlightModalOpen] = useState(false);
  const [previewHighlight, setPreviewHighlight] = useState(null);
  const [savingHighlight, setSavingHighlight] = useState(false);

  // ── Load categories ───────────────────────────────────────────────────────
  useEffect(() => {
    workshopsService.getCategories().then((cats) => {
      setCategories(cats);
      if (!isEditMode && cats.length > 0 && !categoryId) {
        setCategoryId(cats[0].id);
      }
    }).catch((err) => {
      console.error("Failed to load categories:", err);
    });
  }, [isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load workshop for edit mode ───────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode) return;
    workshopsService
      .getWorkshopBySlugOrId(identifier, true)
      .then((ws) => {
        if (!ws) {
          toast.error("Workshop not found");
          navigate("/admin/workshops");
          return;
        }

        setCurrentWorkshopId(ws.id);
        if (ws.slug && identifier !== ws.slug) {
          navigate(`/admin/workshops/${ws.slug}/edit`, { replace: true });
        }

        // Basic
        setTitle(ws.title || "");
        setSlug(ws.slug || "");
        setCategoryId(ws.category_id || "");
        setMode(ws.mode || "Online");
        setVenue(ws.venue || "");
        setDate(ws.date || "");
        setStartTime(ws.start_time || "");
        setEndTime(ws.end_time || "");
        setDuration(ws.duration || "");
        setStatus(ws.status || "Draft");

        // Curriculum
        setShortDescription(ws.short_description || "");
        setDescription(ws.description || "");
        setLearningOutcomesText(
          Array.isArray(ws.learning_outcomes) ? ws.learning_outcomes.join("\n") : ""
        );
        setPrerequisitesText(
          Array.isArray(ws.prerequisites) ? ws.prerequisites.join("\n") : ""
        );

        // Instructors — load from the array returned by the service
        const instList = Array.isArray(ws.instructors) && ws.instructors.length > 0
          ? ws.instructors.map((inst) => ({
              id: inst.id || "",
              name: inst.name || "",
              role: inst.role || "",
              bio: inst.bio || "",
              avatar_url: inst.avatar_url || "",
              linkedin_url: inst.linkedin_url || "",
              is_lead: Boolean(inst.is_lead),
            }))
          : ws.instructor
          ? [{ id: ws.instructor.id || "", name: ws.instructor.name || "", role: ws.instructor.role || "", bio: ws.instructor.bio || "", avatar_url: ws.instructor.avatar_url || "", linkedin_url: ws.instructor.linkedin_url || "", is_lead: true }]
          : [{ ...BLANK_INSTRUCTOR, is_lead: true }];
        setInstructors(instList);

        // Agenda
        if (Array.isArray(ws.agenda) && ws.agenda.length > 0) {
          setAgendaItems(
            ws.agenda.map((a) => ({
              topic: a.topic || "",
              description: a.description || "",
            }))
          );
        }

        // Internal
        setMeetingLink(ws.meeting_link || "");
        setIsFeatured(Boolean(ws.is_featured));
        setCertificateAvailable(Boolean(ws.certificate_available));
        setCoverImage(ws.cover_image || "");
        setIsFree(Boolean(ws.is_free));
        setPrice(ws.price || 0);
        setCapacity(ws.capacity || 150);
        setShowInPromotionalModal(Boolean(ws.show_in_promotional_modal));
        setShowHighlights(ws.show_highlights !== undefined ? Boolean(ws.show_highlights) : true);
        if (Array.isArray(ws.highlights)) {
          setHighlights(ws.highlights);
        }

        // Registration deadline — strip to date-local string for input
        if (ws.registration_deadline) {
          try {
            const d = new Date(ws.registration_deadline);
            // Format: YYYY-MM-DDTHH:mm for datetime-local input
            const pad = (n) => String(n).padStart(2, "0");
            setRegistrationDeadline(
              `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
            );
          } catch {
            setRegistrationDeadline("");
          }
        }
      })
      .catch((err) => {
        console.error("Error loading workshop:", err);
        toast.error("Failed to load workshop details");
      })
      .finally(() => setLoading(false));
  }, [identifier, isEditMode, navigate]);

  // ── Instructor list helpers ────────────────────────────────────────────────
  const updateInstructor = useCallback((idx, field, value) => {
    setInstructors((prev) =>
      prev.map((inst, i) => (i === idx ? { ...inst, [field]: value } : inst))
    );
  }, []);

  const addInstructor = useCallback(() => {
    setInstructors((prev) => [...prev, { ...BLANK_INSTRUCTOR }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeInstructor = useCallback((idx) => {
    setInstructors((prev) => {
      if (prev.length <= 1) return prev; // always keep at least one
      const next = prev.filter((_, i) => i !== idx);
      // Ensure one is lead
      if (!next.some((i) => i.is_lead)) next[0] = { ...next[0], is_lead: true };
      return next;
    });
  }, []);

  const setLeadInstructor = useCallback((idx) => {
    setInstructors((prev) =>
      prev.map((inst, i) => ({ ...inst, is_lead: i === idx }))
    );
  }, []);

  const handleFetchLinkedIn = useCallback(async (idx) => {
    const url = instructors[idx]?.linkedin_url?.trim();
    if (!url) {
      toast.error("Please enter a LinkedIn profile URL first");
      return;
    }
    try {
      setFetchingLinkedInIdx(idx);
      const profile = await linkedinService.fetchProfile(url);
      setInstructors((prev) =>
        prev.map((inst, i) =>
          i === idx
            ? {
                ...inst,
                name: profile.name || inst.name,
                role: profile.role || inst.role,
                bio: profile.bio || inst.bio,
                avatar_url: profile.avatar_url || inst.avatar_url,
                linkedin_url: profile.linkedin_url || inst.linkedin_url,
              }
            : inst
        )
      );
      toast.success(`Fetched profile for ${profile.name || "instructor"}!`);
    } catch (err) {
      console.error("LinkedIn fetch failed:", err);
      toast.error(err.message || "Failed to fetch LinkedIn profile");
    } finally {
      setFetchingLinkedInIdx(null);
    }
  }, [instructors]);

  // ── Agenda helpers ────────────────────────────────────────────────────────
  const addAgendaItem = useCallback(() => {
    setAgendaItems((prev) => [
      ...prev,
      { topic: "", description: "" },
    ]);
  }, []);

  const updateAgendaItem = useCallback((index, field, value) => {
    setAgendaItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  }, []);

  const removeAgendaItem = useCallback((index) => {
    setAgendaItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const moveAgendaItem = useCallback((fromIndex, direction) => {
    setAgendaItems((prev) => {
      const next = [...prev];
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= next.length) return prev;
      [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
      return next;
    });
  }, []);

  // ── Highlights management helpers ─────────────────────────────────────────
  const refreshHighlights = useCallback(async (workshopId) => {
    const id = workshopId || currentWorkshopId;
    if (!id) return;
    try {
      setLoadingHighlights(true);
      const media = await workshopsService.getWorkshopMedia(id);
      const hl = (media || [])
        .filter((m) => m.media_type === "highlight")
        .map((m) => ({
          id: m.id,
          url: m.storage_path,
          storage_path: m.storage_path,
          media_type: "highlight",
          media_format: m.media_format || (m.video_url ? "video" : "image"),
          video_url: m.video_url || "",
          thumbnail_url: m.thumbnail_url || "",
          alt_text: m.alt_text || "",
          caption: m.caption || "",
          description: m.description || "",
          display_order: m.display_order ?? 0,
          is_published: m.is_published ?? true,
          is_featured: Boolean(m.is_featured),
        }));
      setHighlights(hl);
    } catch (err) {
      console.error("Failed to load workshop highlights:", err);
    } finally {
      setLoadingHighlights(false);
    }
  }, [currentWorkshopId]);

  const handleOpenAddHighlight = () => {
    setEditingHighlight({
      ...BLANK_HIGHLIGHT,
      display_order: highlights.length,
    });
    setIsHighlightModalOpen(true);
  };

  const handleOpenEditHighlight = (hl) => {
    setEditingHighlight({ ...hl });
    setIsHighlightModalOpen(true);
  };

  const handleToggleHighlightPublish = async (hl) => {
    try {
      const newStatus = !hl.is_published;
      await workshopsService.updateWorkshopMedia(hl.id, {
        is_published: newStatus,
      });
      setHighlights((prev) =>
        prev.map((item) => (item.id === hl.id ? { ...item, is_published: newStatus } : item))
      );
      toast.success(newStatus ? "Highlight published" : "Highlight set to draft");
    } catch (err) {
      toast.error("Failed to update status: " + err.message);
    }
  };

  const handleDeleteHighlight = async (hlId) => {
    if (!window.confirm("Are you sure you want to delete this highlight?")) return;
    try {
      await workshopsService.deleteWorkshopMedia(hlId);
      setHighlights((prev) => prev.filter((item) => item.id !== hlId));
      toast.success("Highlight deleted successfully");
    } catch (err) {
      toast.error("Failed to delete highlight: " + err.message);
    }
  };

  const handleMoveHighlight = async (index, direction) => {
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= highlights.length) return;
    const reordered = [...highlights];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIdx, 0, moved);
    setHighlights(reordered);

    try {
      await workshopsService.reorderWorkshopMedia(
        currentWorkshopId,
        reordered.map((item) => item.id)
      );
    } catch (err) {
      toast.error("Failed to reorder: " + err.message);
      refreshHighlights(currentWorkshopId);
    }
  };

  const handleSaveHighlightForm = async (e) => {
    e.preventDefault();
    if (!currentWorkshopId) {
      toast.error("Please save the workshop first before adding highlights.");
      return;
    }
    const isVideo = editingHighlight.media_format === "video";
    const mediaUrl = isVideo ? editingHighlight.video_url : editingHighlight.storage_path;
    if (!mediaUrl || !mediaUrl.trim()) {
      toast.error(isVideo ? "Video URL is required" : "Image URL / Storage Path is required");
      return;
    }

    try {
      setSavingHighlight(true);
      const payload = {
        media_type: "highlight",
        media_format: editingHighlight.media_format,
        storage_path: (editingHighlight.storage_path || "").trim(),
        video_url: (editingHighlight.video_url || "").trim(),
        thumbnail_url: (editingHighlight.thumbnail_url || "").trim(),
        alt_text: (editingHighlight.alt_text || "").trim(),
        caption: (editingHighlight.caption || "").trim(),
        description: (editingHighlight.description || "").trim(),
        display_order: Number(editingHighlight.display_order ?? highlights.length),
        is_published: Boolean(editingHighlight.is_published),
        is_featured: Boolean(editingHighlight.is_featured),
      };

      if (editingHighlight.id) {
        await workshopsService.updateWorkshopMedia(editingHighlight.id, payload);
        toast.success("Highlight updated successfully!");
      } else {
        await workshopsService.createWorkshopMedia(currentWorkshopId, payload);
        toast.success("Highlight created successfully!");
      }

      setIsHighlightModalOpen(false);
      setEditingHighlight(null);
      await refreshHighlights(currentWorkshopId);
    } catch (err) {
      console.error("Save highlight error:", err);
      toast.error(err.message || "Failed to save highlight");
    } finally {
      setSavingHighlight(false);
    }
  };

  const handleTogglePromotionModal = async () => {
    const nextVal = !showInPromotionalModal;
    setShowInPromotionalModal(nextVal);
    if (currentWorkshopId) {
      try {
        await workshopsService.updateWorkshopPromotionSettings(currentWorkshopId, nextVal);
        toast.success(nextVal ? "Added to promotional modal" : "Removed from promotional modal");
      } catch (err) {
        console.error("Failed to update promotional setting:", err);
        toast.error("Failed to update promotional modal setting: " + err.message);
        setShowInPromotionalModal(!nextVal);
      }
    }
  };

  const handleToggleWorkshopHighlights = async () => {
    const nextVal = !showHighlights;
    setShowHighlights(nextVal);
    if (currentWorkshopId) {
      try {
        await workshopsService.updateWorkshopHighlightsSettings(currentWorkshopId, nextVal);
        toast.success(nextVal ? "Post-event highlights enabled" : "Post-event highlights disabled");
      } catch (err) {
        console.error("Failed to update highlights setting:", err);
        toast.error("Failed to update highlights setting: " + err.message);
        setShowHighlights(!nextVal);
      }
    }
  };

  // ── Main save handler ─────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Workshop Title is required");
      setActiveTab("basic");
      return;
    }
    const leadInstructor = instructors.find((i) => i.is_lead) || instructors[0];
    if (!leadInstructor?.name?.trim()) {
      toast.error("Lead instructor name is required");
      setActiveTab("instructor");
      return;
    }
    if (!categoryId) {
      toast.error("Category is required");
      setActiveTab("basic");
      return;
    }

    try {
      setSaving(true);

      // 1. Build workshop payload — instructor_id set after upsertWorkshopInstructors below
      const leadInst = instructors.find((i) => i.is_lead) || instructors[0];
      // Resolve a temporary instructor_id for the initial insert (needed for NOT NULL FK)
      let leadInstructorId = leadInst.id;
      if (!leadInstructorId) {
        const saved = await workshopsService.upsertInstructor({
          name: leadInst.name.trim(),
          role: leadInst.role.trim(),
          bio: leadInst.bio.trim(),
          avatar_url: leadInst.avatar_url.trim(),
          linkedin_url: leadInst.linkedin_url.trim(),
        });
        leadInstructorId = saved.id;
      }

      // 2. Build the main workshop payload
      // is_published is strictly derived from status — no contradictory state allowed
      const isPublished = status === "Published";

      const workshopPayload = {
        title: title.trim(),
        slug: slug.trim() || generateSlug(title),
        short_description: shortDescription.trim() || title.trim(),
        description: description.trim() || shortDescription.trim() || title.trim(),
        category_id: categoryId,               // Real UUID — never a category name
        mode,
        venue: venue.trim(),
        date,
        start_time: startTime,
        end_time: endTime,
        duration: duration.trim(),
        instructor_id: leadInstructorId,      // Real UUID of lead instructor
        cover_image: coverImage.trim(),
        learning_outcomes: learningOutcomesText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        prerequisites: prerequisitesText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        is_free: isFree,
        price: isFree ? 0 : Number(price) || 0,
        capacity: Number(capacity) || 150,
        registration_deadline: registrationDeadline
          ? new Date(registrationDeadline).toISOString()
          : null,
        status,
        is_published: isPublished,              // Strictly derived — no contradictory state
        is_featured: isFeatured,
        certificate_available: certificateAvailable,
        show_in_promotional_modal: showInPromotionalModal,
        show_highlights: showHighlights,
      };

      let savedWorkshop;
      const targetId = currentWorkshopId || paramId;
      if (isEditMode) {
        savedWorkshop = await workshopsService.updateWorkshop(targetId, workshopPayload);
        toast.success("Workshop updated successfully!");
      } else {
        savedWorkshop = await workshopsService.createWorkshop(workshopPayload);
        toast.success("Workshop created successfully!");
      }

      const workshopId = savedWorkshop?.id || targetId;
      if (workshopId && !currentWorkshopId) {
        setCurrentWorkshopId(workshopId);
      }

      // 3. Persist all instructors to junction table (replace-all)
      await workshopsService.upsertWorkshopInstructors(workshopId, instructors.map((inst, i) => ({
        ...inst,
        is_lead: inst.is_lead ?? (i === 0),
      })));

      // 4. Persist agenda items
      await workshopsService.upsertAgendaItems(workshopId, agendaItems);

      // 5. Persist internal/private data
      await workshopsService.upsertInternalData(workshopId, {
        meeting_link: meetingLink,
        internal_notes: internalNotes,
      });

      navigate("/admin/workshops");
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.message || "Failed to save workshop");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkshop = async () => {
    const targetId = currentWorkshopId || paramId;
    if (!targetId) return;

    const confirmMessage =
      `Are you sure you want to COMPLETELY delete "${title || "this workshop"}"?\n\n` +
      `This will permanently remove the workshop along with all its agenda, instructors, highlights, media, and applicant registrations.\n\n` +
      `This action CANNOT be undone.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setDeleting(true);
      await workshopsService.deleteWorkshop(targetId, { permanent: true });
      toast.success("Workshop completely deleted.");
      navigate("/admin/workshops");
    } catch (err) {
      console.error("Delete workshop error:", err);
      toast.error(err.message || "Failed to completely delete workshop");
      setDeleting(false);
    }
  };


  if (loading) {
    return (
      <div className="py-24 text-center text-xs text-slate-500 flex flex-col items-center gap-3 font-medium">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
        <span>Loading workshop editor...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* Top Breadcrumb */}
      <div className="mb-4">
        <Link
          to="/admin/workshops"
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 font-medium transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Workshops List</span>
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            {isEditMode ? "Edit Workshop" : "Create New Workshop"}
          </h1>
          <p className="text-xs text-slate-600 mt-1 font-medium">
            {isEditMode
              ? "Manage schedule, curriculum, agenda, and internal settings."
              : "Define a new workshop with full curriculum, schedule, and instructor."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/workshops")}
            className="text-xs border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium"
          >
            Cancel
          </Button>
          {isEditMode && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDeleteWorkshop}
              disabled={deleting || saving}
              className="text-xs border-rose-200 bg-rose-50/60 text-rose-700 hover:bg-rose-100 hover:text-rose-800 hover:border-rose-300 font-medium gap-1.5 h-9"
              title="Completely delete workshop"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin text-rose-600" />
              ) : (
                <Trash2 className="w-4 h-4 text-rose-600" />
              )}
              <span>Delete</span>
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || deleting}
            className="rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 h-9 shadow-sm"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? "Saving..." : isEditMode ? "Save Changes" : "Create Workshop"}</span>
          </Button>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-4 mb-8 overflow-x-auto">
        <TabButton
          id="instructor"
          active={activeTab === "instructor"}
          onClick={setActiveTab}
          icon={User}
          label={`Instructors (${instructors.length})`}
          dot={instructors.some((i) => i.avatar_url)}
        />
        <TabButton
          id="basic"
          active={activeTab === "basic"}
          onClick={setActiveTab}
          icon={Calendar}
          label="Basic & Schedule"
        />
        <TabButton
          id="curriculum"
          active={activeTab === "curriculum"}
          onClick={setActiveTab}
          icon={BookOpen}
          label="Curriculum"
        />
        <TabButton
          id="agenda"
          active={activeTab === "agenda"}
          onClick={setActiveTab}
          icon={ListOrdered}
          label={`Agenda (${agendaItems.length})`}
        />
        <TabButton
          id="internal"
          active={activeTab === "internal"}
          onClick={setActiveTab}
          icon={Settings2}
          label="Internal & Settings"
        />
        <TabButton
          id="highlights"
          active={activeTab === "highlights"}
          onClick={setActiveTab}
          icon={Film}
          label={`Highlights (${highlights.length})`}
          dot={showHighlights && highlights.length > 0}
        />
      </div>

      {/* ── TAB 1: Instructors & LinkedIn Auto-Fetch ─────────────────── */}
      {activeTab === "instructor" && (
        <div className="space-y-8">
          {/* Header Strip with Add Instructor Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-slate-200 bg-white shadow-xs">
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <span>Workshop Instructors</span>
                <Badge variant="outline" className="text-xs text-cyan-700 bg-cyan-50 border-cyan-200">
                  {instructors.length} {instructors.length === 1 ? "Instructor" : "Instructors"}
                </Badge>
              </h2>
              <p className="text-xs text-slate-600 mt-0.5 font-medium">
                Manage all session trainers. The Lead Instructor is featured primarily and bound to the workshop record.
              </p>
            </div>
            <Button
              type="button"
              onClick={addInstructor}
              className="h-10 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold flex items-center gap-2 flex-shrink-0 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Instructor</span>
            </Button>
          </div>

          {/* Instructors List */}
          <div className="space-y-6">
            {instructors.map((inst, idx) => {
              const isFetching = fetchingLinkedInIdx === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6 relative overflow-hidden transition-all hover:border-slate-300 shadow-xs"
                >
                  {/* Instructor Card Header */}
                  <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-slate-700 px-2.5 py-1 rounded-lg bg-slate-100">
                        Instructor #{idx + 1}
                      </span>
                      {inst.is_lead ? (
                        <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Lead Instructor
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setLeadInstructor(idx)}
                          className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 hover:border-cyan-300 hover:text-cyan-700 transition-colors"
                        >
                          Set as Lead
                        </button>
                      )}
                    </div>

                    {instructors.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeInstructor(idx)}
                        className="h-8 px-2 text-rose-600 hover:bg-rose-50 text-xs gap-1.5 font-medium"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Remove</span>
                      </Button>
                    )}
                  </div>

                  {/* LinkedIn auto-enrichment panel */}
                  <div className="rounded-xl border border-cyan-200 bg-cyan-50/50 p-5 relative overflow-hidden">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 h-6 rounded-lg bg-cyan-100 flex items-center justify-center">
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
                        Auto-Fetch from LinkedIn
                      </h3>
                    </div>
                    <p className="text-[11px] text-slate-600 mb-3 font-medium">
                      Paste this instructor's public LinkedIn profile URL. We auto-extract photo, name, role, and bio.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2.5">
                      <div className="relative flex-1">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" />
                        </div>
                        <Input
                          type="url"
                          placeholder="https://www.linkedin.com/in/username"
                          value={inst.linkedin_url || ""}
                          onChange={(e) => updateInstructor(idx, "linkedin_url", e.target.value)}
                          className="pl-9 h-10 rounded-xl bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs focus:border-cyan-500"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={() => handleFetchLinkedIn(idx)}
                        disabled={isFetching}
                        className="h-10 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold flex items-center gap-1.5 flex-shrink-0"
                      >
                        {isFetching ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        <span>{isFetching ? "Fetching..." : "Fetch Profile"}</span>
                      </Button>
                    </div>

                    <div className="mt-2.5 flex items-center gap-2 flex-wrap text-[11px] text-slate-500 font-medium">
                      <span>Quick autofill:</span>
                      <button
                        type="button"
                        onClick={() => updateInstructor(idx, "linkedin_url", "https://www.linkedin.com/in/udaysingh902")}
                        className="text-cyan-600 hover:underline font-semibold"
                      >
                        Uday Pratap Singh
                      </button>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={() => updateInstructor(idx, "linkedin_url", "https://www.linkedin.com/in/ayush-yadav-8baa5a261/")}
                        className="text-cyan-600 hover:underline font-semibold"
                      >
                        Ayush Yadav
                      </button>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={() => updateInstructor(idx, "linkedin_url", "https://www.linkedin.com/in/arpitmanitripathi")}
                        className="text-cyan-600 hover:underline font-semibold"
                      >
                        Arpit Mani Tripathi
                      </button>
                    </div>
                  </div>

                  {/* Live preview */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="relative w-14 h-14 rounded-2xl overflow-hidden border border-slate-200 bg-white flex-shrink-0 shadow-xs">
                      {inst.avatar_url || (inst.linkedin_url ? getLinkedInAvatarUrl(inst.linkedin_url) : null) ? (
                        <img
                          src={inst.avatar_url || getLinkedInAvatarUrl(inst.linkedin_url)}
                          alt={inst.name || "Instructor"}
                          className="w-full h-full object-cover object-center"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-cyan-50 text-cyan-700 font-bold text-base">
                          {getInitials(inst.name)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-bold text-slate-900 tracking-tight">
                          {inst.name || "Instructor Name"}
                        </h4>
                        {inst.linkedin_url && (
                          <a
                            href={inst.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-[#0A66C2] hover:underline font-semibold"
                          >
                            <Linkedin className="w-3 h-3" />
                            <span>LinkedIn</span>
                            <ExternalLink className="w-2.5 h-2.5 text-slate-400" />
                          </a>
                        )}
                      </div>
                      <p className="text-xs text-cyan-600 font-semibold mt-0.5">
                        {inst.role || "Role / Title"}
                      </p>
                      {inst.bio && (
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2 leading-relaxed font-medium">
                          {inst.bio}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Form fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">
                        Full Name {inst.is_lead && <span className="text-rose-600">*</span>}
                      </Label>
                      <Input
                        value={inst.name || ""}
                        onChange={(e) => updateInstructor(idx, "name", e.target.value)}
                        placeholder="e.g. Uday Pratap Singh"
                        className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Role / Title</Label>
                      <Input
                        value={inst.role || ""}
                        onChange={(e) => updateInstructor(idx, "role", e.target.value)}
                        placeholder="e.g. Full Stack Developer"
                        className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Profile Photo URL</Label>
                      <Input
                        type="url"
                        value={inst.avatar_url || ""}
                        onChange={(e) => updateInstructor(idx, "avatar_url", e.target.value)}
                        placeholder="https://media.licdn.com/dms/image/..."
                        className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono focus:bg-white"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Instructor Bio</Label>
                      <Textarea
                        rows={2}
                        value={inst.bio || ""}
                        onChange={(e) => updateInstructor(idx, "bio", e.target.value)}
                        placeholder="Short bio covering industry expertise, architecture depth, and hands-on background..."
                        className="rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs leading-relaxed focus:bg-white"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add Another Instructor Button */}
          <Button
            type="button"
            variant="outline"
            onClick={addInstructor}
            className="w-full py-6 border-dashed border-slate-300 hover:border-cyan-400 hover:bg-cyan-50 text-slate-700 hover:text-cyan-700 flex items-center justify-center gap-2 rounded-2xl text-xs font-semibold"
          >
            <Plus className="w-4 h-4 text-cyan-600" />
            <span>Add Another Instructor</span>
          </Button>
        </div>
      )}

      {/* ── TAB 2: Basic & Schedule ──────────────────────────────────── */}
      {activeTab === "basic" && (
        <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2 space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Workshop Title <span className="text-rose-600">*</span></Label>
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!isEditMode) setSlug(generateSlug(e.target.value));
                }}
                placeholder="e.g. AI Engineering Workshop — Session 1"
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-semibold focus:bg-white"
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">URL Slug</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="ai-engineering-workshop-session-1"
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono focus:bg-white"
              />
              <p className="text-[10px] text-slate-400 font-medium">/workshops/{slug || "..."}</p>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Category <span className="text-rose-600">*</span></Label>
              {categories.length > 0 ? (
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger className="w-full h-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-xs px-3 focus:outline-none focus:border-cyan-500 font-medium">
                    <SelectValue placeholder="Select a Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-slate-200 text-slate-900 shadow-lg">
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="text-xs">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-10 rounded-xl bg-slate-50 border border-slate-200 px-3 flex items-center text-xs text-slate-400 font-medium">
                  Loading categories...
                </div>
              )}
            </div>

            {/* Mode */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Delivery Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-full h-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-xs px-3 focus:outline-none focus:border-cyan-500 font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200 text-slate-900 shadow-lg">
                  {WORKSHOP_MODES.map((m) => (
                    <SelectItem key={m} value={m} className="text-xs">
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full h-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-xs px-3 focus:outline-none focus:border-cyan-500 font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200 text-slate-900 shadow-lg">
                  {WORKSHOP_STATUSES.map((st) => (
                    <SelectItem key={st} value={st} className="text-xs">
                      {st}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 font-medium">
                is_published = {status === "Published" ? "true ✅" : "false"}
              </p>
            </div>

            {/* Venue */}
            <div className="md:col-span-2 space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Venue / Location</Label>
              <Input
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="e.g. Live Interactive Session (Virtual)"
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Session Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
              />
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Duration</Label>
              <Input
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g. 1.5 Hours"
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
              />
            </div>

            {/* Start time */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Start Time</Label>
              <Input
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder="07:00 PM"
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
              />
            </div>

            {/* End time */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">End Time</Label>
              <Input
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                placeholder="08:30 PM"
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: Curriculum ────────────────────────────────────────── */}
      {activeTab === "curriculum" && (
        <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Short Summary</Label>
            <Textarea
              rows={2}
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder="Concise 1–2 sentence workshop overview shown on listing cards..."
              className="rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Full Description</Label>
            <Textarea
              rows={8}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Full workshop description. Supports plain text. Describe the session scope, learning journey, target audience, and career relevance..."
              className="rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs leading-relaxed focus:bg-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">
              Learning Outcomes{" "}
              <span className="text-slate-400 font-normal">(one item per line)</span>
            </Label>
            <Textarea
              rows={6}
              value={learningOutcomesText}
              onChange={(e) => setLearningOutcomesText(e.target.value)}
              placeholder={"Understand what AI Engineering actually means\nArchitecture of Retrieval-Augmented Generation (RAG)\nBuilding autonomous AI agent reasoning loops"}
              className="rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono focus:bg-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">
              Prerequisites{" "}
              <span className="text-slate-400 font-normal">(one item per line)</span>
            </Label>
            <Textarea
              rows={3}
              value={prerequisitesText}
              onChange={(e) => setPrerequisitesText(e.target.value)}
              placeholder={"Basic programming knowledge in Python or JavaScript\nFamiliarity with web APIs"}
              className="rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono focus:bg-white"
            />
          </div>
        </div>
      )}

      {/* ── TAB 4: Agenda ────────────────────────────────────────────── */}
      {activeTab === "agenda" && (
        <div className="space-y-6">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-600 font-medium">
                Define the timed schedule shown on the workshop detail page.
                Changes save when you click <strong className="text-slate-900">Save Changes</strong>.
              </p>
            </div>
            <Button
              type="button"
              onClick={addAgendaItem}
              size="sm"
              className="rounded-xl bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 text-xs font-semibold gap-2 h-9 flex-shrink-0"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Item
            </Button>
          </div>

          {/* Items */}
          {agendaItems.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 flex flex-col items-center gap-3 text-center shadow-xs">
              <ListOrdered className="w-8 h-8 text-slate-300" />
              <p className="text-xs text-slate-500 font-medium max-w-xs">
                No agenda items yet. Click <strong className="text-slate-700">Add Item</strong> to define the workshop schedule.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {agendaItems.map((item, index) => (
                <AgendaItemRow
                  key={index}
                  item={item}
                  index={index}
                  total={agendaItems.length}
                  onChange={updateAgendaItem}
                  onRemove={removeAgendaItem}
                  onMoveUp={(i) => moveAgendaItem(i, -1)}
                  onMoveDown={(i) => moveAgendaItem(i, 1)}
                />
              ))}
            </div>
          )}

          {agendaItems.length > 0 && (
            <Button
              type="button"
              onClick={addAgendaItem}
              variant="outline"
              size="sm"
              className="w-full rounded-xl border-dashed border-slate-300 bg-white text-slate-700 hover:text-cyan-700 hover:bg-cyan-50 text-xs gap-2 font-medium"
            >
              <Plus className="w-3.5 h-3.5 text-cyan-600" />
              Add Another Item
            </Button>
          )}
        </div>
      )}

      {/* ── TAB 5: Internal & Settings ───────────────────────────────── */}
      {activeTab === "internal" && (
        <div className="space-y-8">
          {/* Meeting link — admin only */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-6 shadow-xs">
            <div className="flex items-center gap-2.5 mb-4">
              <Lock className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-bold text-slate-900">Internal / Admin-Only Data</h2>
              <Badge variant="outline" className="text-[10px] text-amber-700 bg-amber-100 border-amber-300 font-semibold">
                Never public
              </Badge>
            </div>
            <p className="text-xs text-slate-600 mb-5 font-medium">
              This data is stored in <code className="text-amber-700 font-semibold">workshop_internal_data</code> and is
              never returned through public Workshop API queries. Meeting links remain private.
            </p>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Meeting / Session Link</Label>
                <Input
                  type="url"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="https://meet.google.com/... or https://zoom.us/j/..."
                  className="h-10 rounded-xl bg-white border-slate-200 text-slate-900 text-xs font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Internal Notes</Label>
                <Textarea
                  rows={3}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Internal production notes, host keys, backup plans, contact escalation..."
                  className="rounded-xl bg-white border-slate-200 text-slate-900 text-xs"
                />
              </div>
            </div>
          </div>

          {/* Cover image */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5 shadow-xs">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-cyan-600" />
              Cover Image
            </h2>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Cover Image URL</Label>
              <Input
                type="url"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="https://images.unsplash.com/..."
                className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono focus:bg-white"
              />
            </div>

            {coverImage && (
              <div className="rounded-xl overflow-hidden border border-slate-200 aspect-video max-h-48">
                <img
                  src={coverImage}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
          </div>

          {/* Registration settings */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5 shadow-xs">
            <h2 className="text-sm font-bold text-slate-900">Registration Settings</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Capacity */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Capacity (seats)</Label>
                <Input
                  type="number"
                  min="1"
                  value={capacity}
                  onChange={(e) => setCapacity(Math.max(1, Number(e.target.value)))}
                  className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
                />
                <p className="text-[10px] text-slate-400 font-medium">
                  registration_count is derived from actual registrations — not manually adjustable here.
                </p>
              </div>

              {/* Registration deadline */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Registration Deadline</Label>
                <Input
                  type="datetime-local"
                  value={registrationDeadline}
                  onChange={(e) => setRegistrationDeadline(e.target.value)}
                  className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
                />
              </div>

              {/* Free / paid toggle */}
              <div className="space-y-3">
                <Label className="text-xs font-semibold text-slate-700">Pricing</Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsFree(true)}
                    className={cn(
                      "px-4 py-2 rounded-xl text-xs font-semibold transition-colors border",
                      isFree
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900"
                    )}
                  >
                    Free
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsFree(false)}
                    className={cn(
                      "px-4 py-2 rounded-xl text-xs font-semibold transition-colors border",
                      !isFree
                        ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900"
                    )}
                  >
                    Paid
                  </button>
                </div>
                {!isFree && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Price (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={price}
                      onChange={(e) => setPrice(Number(e.target.value))}
                      className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs focus:bg-white"
                    />
                  </div>
                )}
              </div>

              {/* Toggles */}
              <div className="space-y-4">
                <Label className="text-xs font-semibold text-slate-700">Workshop Flags</Label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div
                      onClick={() => setIsFeatured((v) => !v)}
                      className={cn(
                        "w-10 h-5 rounded-full relative transition-colors cursor-pointer",
                        isFeatured ? "bg-cyan-600" : "bg-slate-200"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                          isFeatured ? "left-5" : "left-0.5"
                        )}
                      />
                    </div>
                    <span className="text-xs text-slate-700 group-hover:text-slate-900 font-medium transition-colors">
                      Featured on homepage spotlight
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div
                      onClick={() => setCertificateAvailable((v) => !v)}
                      className={cn(
                        "w-10 h-5 rounded-full relative transition-colors cursor-pointer",
                        certificateAvailable ? "bg-cyan-600" : "bg-slate-200"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                          certificateAvailable ? "left-5" : "left-0.5"
                        )}
                      />
                    </div>
                    <span className="text-xs text-slate-700 group-hover:text-slate-900 font-medium transition-colors">
                      Certificate of completion available
                    </span>
                  </label>

                  <div
                    onClick={handleTogglePromotionModal}
                    className="flex items-center gap-3 cursor-pointer group select-none"
                  >
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showInPromotionalModal}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePromotionModal();
                      }}
                      className={cn(
                        "w-10 h-5 rounded-full relative transition-colors cursor-pointer flex-shrink-0",
                        showInPromotionalModal ? "bg-cyan-600" : "bg-slate-200"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                          showInPromotionalModal ? "left-5" : "left-0.5"
                        )}
                      />
                    </button>
                    <div className="flex flex-col">
                      <span className="text-xs text-slate-700 group-hover:text-slate-900 font-medium transition-colors">
                        Show in global promotional popup modal
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Displays in the 4-second delayed visitor invitation popup
                      </span>
                    </div>
                  </div>

                  <div
                    onClick={handleToggleWorkshopHighlights}
                    className="flex items-center gap-3 cursor-pointer group select-none"
                  >
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showHighlights}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleWorkshopHighlights();
                      }}
                      className={cn(
                        "w-10 h-5 rounded-full relative transition-colors cursor-pointer flex-shrink-0",
                        showHighlights ? "bg-cyan-600" : "bg-slate-200"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                          showHighlights ? "left-5" : "left-0.5"
                        )}
                      />
                    </button>
                    <div className="flex flex-col">
                      <span className="text-xs text-slate-700 group-hover:text-slate-900 font-medium transition-colors">
                        Enable post-event session highlights gallery
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Shows curated photos & videos publicly once session has concluded
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            {isEditMode && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-rose-800 uppercase tracking-wider mb-1">
                      Danger Zone
                    </h4>
                    <p className="text-xs text-slate-600 font-medium">
                      Permanently delete this workshop and all associated curriculum, media, and applicant registrations.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDeleteWorkshop}
                    disabled={deleting || saving}
                    className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100 hover:text-rose-800 hover:border-rose-300 font-medium gap-1.5 h-9 text-xs flex-shrink-0"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin text-rose-600" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    )}
                    <span>Delete Workshop</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 6: Post-Event Session Highlights ──────────────────────── */}
      {activeTab === "highlights" && (
        <div className="space-y-6">
          {!isEditMode || !currentWorkshopId ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-xs">
              <div className="w-12 h-12 rounded-2xl bg-cyan-50 border border-cyan-200 text-cyan-600 flex items-center justify-center mx-auto mb-3">
                <Film className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-1">Save Workshop First</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto mb-4 font-medium">
                Please create and save this workshop record before curating and uploading post-event session highlights.
              </p>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="h-9 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold shadow-sm"
              >
                {saving ? "Saving..." : "Save Workshop Now"}
              </Button>
            </div>
          ) : (
            <>
              {/* Highlights Management Banner */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-slate-200 bg-white shadow-xs">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5">
                    <h2 className="text-base font-bold text-slate-900 tracking-tight">
                      Post-Event Session Highlights
                    </h2>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] font-semibold",
                        showHighlights
                          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                          : "text-slate-500 bg-slate-100 border-slate-200"
                      )}
                    >
                      {showHighlights ? "Gallery Enabled" : "Gallery Disabled"}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 font-medium">
                    Curate photos, live code screenshots, and video recordings. Highlights are displayed on the public workshop page strictly once the event has concluded.
                  </p>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleToggleWorkshopHighlights}
                    className={cn(
                      "px-3 py-2 rounded-xl text-xs font-semibold border transition-colors",
                      showHighlights
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                        : "bg-slate-100 text-slate-600 border-slate-200 hover:text-slate-900"
                    )}
                  >
                    {showHighlights ? "Disable Highlights" : "Enable Highlights"}
                  </button>

                  <Button
                    type="button"
                    onClick={handleOpenAddHighlight}
                    className="h-9 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Highlight</span>
                  </Button>
                </div>
              </div>

              {/* Status Note on Lifecycle Eligibility */}
              <div className="p-4 rounded-xl bg-cyan-50/60 border border-cyan-200/80 text-xs text-cyan-900 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-0.5">
                  <span className="font-semibold">Lifecycle Visibility Rule:</span>
                  <p className="text-cyan-800 text-[11px] leading-relaxed">
                    Published highlights are visible to visitors only when this workshop is marked <strong>Completed</strong> (or when current time has surpassed the scheduled session end time) and the <strong>show_highlights</strong> toggle is active. Pre-event visitors will not see this section.
                  </p>
                </div>
              </div>

              {/* Highlights List */}
              {loadingHighlights ? (
                <div className="py-16 text-center text-xs text-slate-500 flex flex-col items-center gap-2.5">
                  <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
                  <span>Loading session highlights...</span>
                </div>
              ) : highlights.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 flex flex-col items-center gap-3 text-center shadow-xs">
                  <Film className="w-8 h-8 text-slate-300" />
                  <p className="text-xs text-slate-500 font-medium max-w-xs">
                    No highlights added yet. Add photo and video moments to showcase what attendees learned and built during this session.
                  </p>
                  <Button
                    type="button"
                    onClick={handleOpenAddHighlight}
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:text-cyan-700 hover:bg-cyan-50 text-xs font-medium gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5 text-cyan-600" />
                    <span>Add First Highlight</span>
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {highlights.map((hl, index) => {
                    const isVideo = hl.media_format === "video";
                    const previewImg = isVideo
                      ? hl.thumbnail_url || hl.storage_path || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80"
                      : hl.storage_path || hl.url;

                    return (
                      <div
                        key={hl.id || index}
                        className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all hover:border-slate-300 shadow-xs"
                      >
                        {/* Left: Thumbnail + Info */}
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          {/* Reorder Buttons */}
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => handleMoveHighlight(index, -1)}
                              disabled={index === 0}
                              className="p-1 rounded text-slate-400 hover:text-cyan-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                              title="Move up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveHighlight(index, 1)}
                              disabled={index === highlights.length - 1}
                              className="p-1 rounded text-slate-400 hover:text-cyan-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                              title="Move down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Media Thumbnail */}
                          <div
                            onClick={() => setPreviewHighlight(hl)}
                            className="relative w-20 h-14 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 cursor-pointer group"
                            title="Click to preview"
                          >
                            <img
                              src={previewImg}
                              alt={hl.caption || "Highlight thumbnail"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            {isVideo && (
                              <div className="absolute inset-0 bg-slate-900/25 flex items-center justify-center">
                                <div className="w-6 h-6 rounded-full bg-cyan-600 text-white flex items-center justify-center shadow-xs">
                                  <Play className="w-3 h-3 fill-white ml-0.5" />
                                </div>
                              </div>
                            )}
                            <div className="absolute bottom-1 right-1">
                              <Badge
                                variant="secondary"
                                className="text-[8px] font-bold px-1.5 py-0 h-4 bg-white/95 text-slate-800 border border-slate-200/80 shadow-2xs"
                              >
                                {isVideo ? "VIDEO" : "PHOTO"}
                              </Badge>
                            </div>
                          </div>

                          {/* Text info */}
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-xs font-bold text-slate-900 truncate">
                                {hl.caption || "Untitled Highlight"}
                              </h4>
                              {hl.is_featured && (
                                <Badge variant="outline" className="text-[9px] font-semibold text-amber-700 bg-amber-50 border-amber-200">
                                  Featured
                                </Badge>
                              )}
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[9px] font-semibold",
                                  hl.is_published
                                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                    : "text-slate-500 bg-slate-100 border-slate-200"
                                )}
                              >
                                {hl.is_published ? "Published" : "Draft"}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-slate-500 line-clamp-1">
                              {hl.description || (isVideo ? hl.video_url : hl.storage_path)}
                            </p>
                          </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                          {/* Preview Button */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewHighlight(hl)}
                            className="h-8 px-2.5 text-xs text-slate-600 hover:text-cyan-700 hover:bg-cyan-50 font-medium gap-1"
                            title="Preview Highlight"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Preview</span>
                          </Button>

                          {/* Quick Publish Toggle */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleHighlightPublish(hl)}
                            className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
                            title={hl.is_published ? "Hide from public view" : "Publish to visitors"}
                          >
                            {hl.is_published ? "Unpublish" : "Publish"}
                          </Button>

                          {/* Edit Button */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditHighlight(hl)}
                            className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium gap-1"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            <span>Edit</span>
                          </Button>

                          {/* Delete Button */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteHighlight(hl.id)}
                            className="h-8 px-2 text-rose-600 hover:bg-rose-50 text-xs font-medium"
                            title="Delete Highlight"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Highlight Add/Edit Dialog ─────────────────────────────────── */}
      <Dialog open={isHighlightModalOpen} onOpenChange={setIsHighlightModalOpen}>
        <DialogContent className="max-w-lg rounded-2xl bg-white border border-slate-200 text-slate-900 p-6 shadow-xl">
          <DialogHeader className="space-y-1 pb-2 border-b border-slate-100">
            <DialogTitle className="text-base font-bold text-slate-900">
              {editingHighlight?.id ? "Edit Session Highlight" : "Add Session Highlight"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 font-medium">
              Curate photo or video moments captured during the workshop session.
            </DialogDescription>
          </DialogHeader>

          {editingHighlight && (
            <form onSubmit={handleSaveHighlightForm} className="space-y-4 pt-2">
              {/* Media Format Selector */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Media Format</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setEditingHighlight((prev) => ({ ...prev, media_format: "image" }))
                    }
                    className={cn(
                      "flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs font-semibold transition-colors",
                      editingHighlight.media_format === "image"
                        ? "bg-cyan-50 text-cyan-700 border-cyan-300 shadow-2xs"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                    )}
                  >
                    <ImageIcon className="w-4 h-4 text-cyan-600" />
                    <span>Photo / Screenshot</span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setEditingHighlight((prev) => ({ ...prev, media_format: "video" }))
                    }
                    className={cn(
                      "flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs font-semibold transition-colors",
                      editingHighlight.media_format === "video"
                        ? "bg-cyan-50 text-cyan-700 border-cyan-300 shadow-2xs"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                    )}
                  >
                    <Film className="w-4 h-4 text-cyan-600" />
                    <span>Video Recording</span>
                  </button>
                </div>
              </div>

              {/* Photo specific: Image URL / Storage path */}
              {editingHighlight.media_format === "image" ? (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Image URL / Path</Label>
                  <Input
                    type="url"
                    value={editingHighlight.storage_path || ""}
                    onChange={(e) =>
                      setEditingHighlight((prev) => ({ ...prev, storage_path: e.target.value }))
                    }
                    placeholder="https://images.unsplash.com/... or storage path"
                    className="h-9 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono"
                    required
                  />
                  {editingHighlight.storage_path && (
                    <div className="rounded-xl overflow-hidden border border-slate-200 aspect-video max-h-36 bg-slate-50">
                      <img
                        src={editingHighlight.storage_path}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                /* Video specific: Video URL + Thumbnail */
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">
                      Video URL (YouTube, Vimeo, MP4)
                    </Label>
                    <Input
                      type="url"
                      value={editingHighlight.video_url || ""}
                      onChange={(e) =>
                        setEditingHighlight((prev) => ({ ...prev, video_url: e.target.value }))
                      }
                      placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                      className="h-9 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">
                      Thumbnail Poster URL (optional)
                    </Label>
                    <Input
                      type="url"
                      value={editingHighlight.thumbnail_url || ""}
                      onChange={(e) =>
                        setEditingHighlight((prev) => ({ ...prev, thumbnail_url: e.target.value }))
                      }
                      placeholder="https://images.unsplash.com/... for video cover card"
                      className="h-9 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs font-mono"
                    />
                  </div>
                </div>
              )}

              {/* Caption */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Caption / Title</Label>
                <Input
                  value={editingHighlight.caption || ""}
                  onChange={(e) =>
                    setEditingHighlight((prev) => ({ ...prev, caption: e.target.value }))
                  }
                  placeholder="e.g. Autonomous Agent Reasoning Loop Demo"
                  className="h-9 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Description / Takeaway</Label>
                <Textarea
                  rows={2}
                  value={editingHighlight.description || ""}
                  onChange={(e) =>
                    setEditingHighlight((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="e.g. Live coding demonstration of agent tool calling and state management."
                  className="rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-xs"
                />
              </div>

              {/* Switches: Published and Featured */}
              <div className="grid grid-cols-2 gap-4 pt-1">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    onClick={() =>
                      setEditingHighlight((prev) => ({ ...prev, is_published: !prev.is_published }))
                    }
                    className={cn(
                      "w-9 h-5 rounded-full relative transition-colors cursor-pointer",
                      editingHighlight.is_published ? "bg-cyan-600" : "bg-slate-200"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                        editingHighlight.is_published ? "left-4.5" : "left-0.5"
                      )}
                    />
                  </div>
                  <span className="text-xs text-slate-700 font-medium">Published</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    onClick={() =>
                      setEditingHighlight((prev) => ({ ...prev, is_featured: !prev.is_featured }))
                    }
                    className={cn(
                      "w-9 h-5 rounded-full relative transition-colors cursor-pointer",
                      editingHighlight.is_featured ? "bg-cyan-600" : "bg-slate-200"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                        editingHighlight.is_featured ? "left-4.5" : "left-0.5"
                      )}
                    />
                  </div>
                  <span className="text-xs text-slate-700 font-medium">Featured Highlight</span>
                </label>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsHighlightModalOpen(false)}
                  className="rounded-xl text-xs border-slate-200 text-slate-700 hover:bg-slate-100"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={savingHighlight}
                  className="rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold h-9 shadow-sm"
                >
                  {savingHighlight ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : null}
                  <span>{editingHighlight.id ? "Update Highlight" : "Add Highlight"}</span>
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Highlight Lightbox Preview Dialog ─────────────────────────── */}
      <Dialog
        open={previewHighlight !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewHighlight(null);
        }}
      >
        <DialogContent className="max-w-3xl rounded-2xl bg-white border border-slate-200 text-slate-900 p-6 shadow-xl overflow-hidden">
          <DialogHeader className="space-y-1 pb-3 border-b border-slate-100">
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan-600" />
              <span>{previewHighlight?.caption || "Highlight Preview"}</span>
            </DialogTitle>
            {previewHighlight?.description && (
              <DialogDescription className="text-xs text-slate-500 font-medium">
                {previewHighlight.description}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="pt-3">
            {previewHighlight?.media_format === "video" ? (
              <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-slate-950 border border-slate-200 shadow-inner">
                {getEmbedUrl(previewHighlight.video_url) ? (
                  <iframe
                    src={getEmbedUrl(previewHighlight.video_url)}
                    title={previewHighlight.caption || "Video Highlight"}
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={previewHighlight.video_url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            ) : (
              <div className="relative max-h-[65vh] flex items-center justify-center rounded-xl overflow-hidden bg-slate-50 border border-slate-200 p-2">
                <img
                  src={previewHighlight?.storage_path || previewHighlight?.url}
                  alt={previewHighlight?.caption || "Highlight"}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-xs"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
